import { create } from 'zustand'

/**
 * Tracks whether the backend is currently in deployment-maintenance mode.
 *
 * Source of truth: the axios response interceptor in `lib/api.ts`.
 *   - HTTP 503 with body `{ error: "maintenance" }` from nginx → `setActive(true)`
 *   - Any subsequent successful (2xx) response → `setActive(false)`
 *
 * Components subscribe to this store to render maintenance UX
 * (e.g. `MaintenanceBanner` at the top of the app shell).
 */
interface MaintenanceState {
  isActive: boolean
  /** Wall-clock time at which we entered maintenance, ms since epoch. */
  enteredAt: number | null
  setActive: (active: boolean) => void
}

export const useMaintenanceStore = create<MaintenanceState>((set, get) => ({
  isActive: false,
  enteredAt: null,
  setActive: (active) => {
    // No-op when state is unchanged — keeps `enteredAt` stable across many
    // 503s in a row, so the banner doesn't reset its "since" timer on every
    // retry.
    if (get().isActive === active) return
    set({
      isActive: active,
      enteredAt: active ? Date.now() : null,
    })
  },
}))
